import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to auto-refresh data when user returns to the app
 * and optionally poll at intervals while app is visible
 * 
 * @param {Function} refreshFn - Function to call to refresh data
 * @param {Object} options - Configuration options
 * @param {boolean} options.refreshOnReturn - Refresh when user returns to app (default: true)
 * @param {number} options.pollInterval - Polling interval in ms (default: 0 = disabled)
 * @param {boolean} options.enabled - Enable/disable the hook (default: true)
 */
export function useAutoRefresh(refreshFn, options = {}) {
  const {
    refreshOnReturn = true,
    pollInterval = 0,
    enabled = true
  } = options;

  const lastRefreshRef = useRef(Date.now());
  const pollTimerRef = useRef(null);

  const doRefresh = useCallback(() => {
    if (refreshFn && typeof refreshFn === 'function') {
      console.log('[AutoRefresh] Refreshing data...');
      lastRefreshRef.current = Date.now();
      refreshFn();
    }
  }, [refreshFn]);

  useEffect(() => {
    if (!enabled) return;

    // Handle visibility change (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && refreshOnReturn) {
        // Only refresh if it's been more than 10 seconds since last refresh
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        if (timeSinceLastRefresh > 10000) {
          console.log('[AutoRefresh] User returned to app, refreshing...');
          doRefresh();
        }
      }
    };

    // Handle page focus (for desktop browsers)
    const handleFocus = () => {
      if (refreshOnReturn) {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        if (timeSinceLastRefresh > 10000) {
          console.log('[AutoRefresh] Window focused, refreshing...');
          doRefresh();
        }
      }
    };

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Set up polling if enabled (only while visible)
    if (pollInterval > 0) {
      const startPolling = () => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
        }
        pollTimerRef.current = setInterval(() => {
          if (document.visibilityState === 'visible') {
            console.log('[AutoRefresh] Polling refresh...');
            doRefresh();
          }
        }, pollInterval);
      };

      const stopPolling = () => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      };

      // Start polling if currently visible
      if (document.visibilityState === 'visible') {
        startPolling();
      }

      // Pause/resume polling based on visibility
      const handlePollVisibility = () => {
        if (document.visibilityState === 'visible') {
          startPolling();
        } else {
          stopPolling();
        }
      };

      document.addEventListener('visibilitychange', handlePollVisibility);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.removeEventListener('visibilitychange', handlePollVisibility);
        window.removeEventListener('focus', handleFocus);
        stopPolling();
      };
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, refreshOnReturn, pollInterval, doRefresh]);
}

export default useAutoRefresh;
